import { useRef } from 'react';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import AnimatedHeight from 'react-animate-height';
import { Trans, useTranslation } from 'react-i18next';
import { DiseaseOption, useDiseasesInfiniteList } from '@data/hooks';
import {
  Button,
  AntdForm,
  AntdFormInstance,
  AntdGrid,
  AntdRadio,
  AntdTypography,
  AsyncSelect,
  COLOR_BASE_300,
  useTheme,
} from '@ui';
import { usePrefillDisease } from './hooks';
import { SignUpFormValues } from './index';

const { Text } = AntdTypography;
const { useBreakpoint } = AntdGrid;

interface DiseaseFormProps {
  form: AntdFormInstance<SignUpFormValues>;
}

export const DiseaseForm = ({ form }: DiseaseFormProps) => {
  const { t } = useTranslation();
  const { md } = useBreakpoint();
  const { textColorDark, bgColorLight } = useTheme();

  const radioRef = useRef<HTMLDivElement>(null);

  const disease1 = AntdForm.useWatch(['diseases', 0], form);
  const disease2 = AntdForm.useWatch(['diseases', 1], form);
  const haveDisease = AntdForm.useWatch('haveDisease', form);

  const { diseaseOptions, fetchNextPage, isLoading, handleSearchChange, searchString } =
    useDiseasesInfiniteList();

  const prefillDisease = (disease: DiseaseOption) => {
    form.setFieldValue(['diseases', 0], disease);
  };

  usePrefillDisease(prefillDisease);

  const trialsCount = (disease1?.trialCount || 0) + (disease2?.trialCount || 0);

  return (
    <AntdForm
      form={form}
      name="disease-form"
      className="sing-up-form"
      layout="vertical"
      autoComplete="off"
      data-testid="disease-form"
      size={md ? 'middle' : 'large'}
      requiredMark={true}
      initialValues={{ haveDisease: true, diseases: [undefined] }}
    >
      <Text className="text-xl font-semibold mb-4 flex">
        {t('sign_up_page.find_trials_that_suit_you')}
      </Text>
      <Text className="mb-4 block">
        <Trans
          t={t}
          i18nKey="sign_up_page.simply_register_text"
          components={{
            a: (
              <a
                href={t('faq.link')}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: textColorDark }}
              />
            ),
          }}
        />
      </Text>
      <AntdForm.Item
        name="haveDisease"
        label={<Text className="font-semibold">{t('sign_up_page.do_you_have_disease')}</Text>}
        rules={[
          () => ({
            validator(_, value) {
              if (value === undefined) {
                return Promise.reject(new Error(t('validations.please_select')));
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <AntdRadio.Group
          onChange={(e) => {
            if (!e.target.value) {
              form.setFieldValue('diseases', undefined);
            } else form.setFieldValue('diseases', [undefined]);
            return e.target.value;
          }}
          className="flex flex-nowrap gap-2"
        >
          <div
            className="p-3 sm:p-[6px] rounded-[100px] border-solid"
            style={{ borderColor: haveDisease ? textColorDark : COLOR_BASE_300 }}
          >
            <AntdRadio
              value={true}
              checked={haveDisease}
              data-testid="have-diseases-true"
              className="m-0"
            >
              {t('common.yes')}
            </AntdRadio>
          </div>
          <div
            className="p-3 sm:p-[6px] rounded-[100px] border-solid"
            style={{ borderColor: haveDisease ? COLOR_BASE_300 : textColorDark }}
          >
            <AntdRadio value={false} checked={!haveDisease} data-testid="have-diseases-false">
              {t('common.no')}
            </AntdRadio>
          </div>
        </AntdRadio.Group>
      </AntdForm.Item>{' '}
      <div ref={radioRef} />
      <AnimatedHeight height={haveDisease ? 'auto' : 0}>
        {haveDisease && (
          <>
            <Text className="text-base-500 flex mb-4">
              {t('sign_up_page.select_max_2_diseases')}
            </Text>
            <AntdForm.List name="diseases">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <AntdForm.Item
                      required={true}
                      key={String(field.key)}
                      label={
                        <Text className="font-semibold">
                          {index === 0
                            ? t('sign_up_page.name_disease')
                            : t('sign_up_page.name_of_other_disease')}
                        </Text>
                      }
                      colon={true}
                      className="custom-form-item mb-2"
                    >
                      <AntdForm.Item
                        {...field}
                        aria-label="disease-form-input"
                        key={Number(index)}
                        rules={[
                          () => ({
                            validator(_, value) {
                              if (!value && haveDisease) {
                                return Promise.reject(new Error(t('validations.required')));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                        validateTrigger={['onChange', 'onBlur']}
                        noStyle
                      >
                        <AsyncSelect
                          isSearchable
                          isClearable
                          closeMenuOnSelect={true}
                          isLoading={!form.getFieldValue(['diseases', index]) && isLoading}
                          onMenuScrollToBottom={() => fetchNextPage()}
                          options={diseaseOptions}
                          className="w-full"
                          placeholder={t('common.type_at_least_3_characters')}
                          onInputChange={(v) => {
                            handleSearchChange(v);
                            if (!v && !disease1 && !disease2) remove(1);
                          }}
                          menuShouldScrollIntoView={true}
                          captureMenuScroll={true}
                          onMenuOpen={() =>
                            radioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                          menuIsOpen={
                            !form.getFieldValue(['diseases', index]) &&
                            !!searchString?.length &&
                            searchString?.length >= 2
                          }
                          maxMenuHeight={md ? undefined : 200}
                          mobileHeight={48}
                          inputId={`diseases-input-${index + 1}`}
                          aria-label="disease-input"
                        />
                      </AntdForm.Item>
                      {fields.length > 1 ? (
                        <Button
                          icon={<CloseOutlined className="text-error" />}
                          type="text"
                          size="small"
                          onClick={() => remove(field.name)}
                          data-testid={`remove-disease-btn-${index}`}
                          className="px-2"
                        />
                      ) : null}
                    </AntdForm.Item>
                  ))}

                  {!!fields.length && fields.length < 2 && (
                    <AntdForm.Item className="mb-2">
                      <Button
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        className="w-full h-[48px] sm:h-auto"
                        data-testid="add-disease-btn"
                        disabled={!disease1}
                      >
                        {t('sign_up_page.add_new_disease')}
                      </Button>
                    </AntdForm.Item>
                  )}
                </>
              )}
            </AntdForm.List>

            {(disease1 || disease2) && (
              <Text
                style={{ color: textColorDark, backgroundColor: bgColorLight }}
                className="p-1 rounded-lg md:rounded-large flex w-full justify-center"
              >
                {trialsCount < 10
                  ? t('sign_up_page.less_than_10_trials_on_recruiting')
                  : t('sign_up_page.trials_on_recruiting_other', { count: trialsCount })}
              </Text>
            )}
          </>
        )}
      </AnimatedHeight>
      <Button
        type="primary"
        className="w-full mt-2 h-[48px] sm:h-auto"
        htmlType="submit"
        data-testid="next-step-btn"
      >
        {t('common.next')}
      </Button>
    </AntdForm>
  );
};
